import React, { useState, useEffect } from 'react';
import { ShieldCheck, Loader2 } from 'lucide-react';
import axios from 'axios';
import Button from '../Ui/Button';
import Input from '../Ui/Input';
import Card from '../Ui/Card';
import { AdminLoginProps } from '../types';

// API base URL
const API_BASE_URL = 'https://wehireindia.com/wehire_api';

const AdminLogin = ({ onLogin, theme }: AdminLoginProps & { theme?: 'light' | 'dark' }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [touched, setTouched] = useState({
        email: false,
        password: false
    });

    // Email validation
    const validateEmail = (email: string) => {
        if (!email) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            return 'Please enter a valid email address';
        }
        return '';
    };

    // Password validation
    const validatePassword = (password: string) => {
        if (!password) return 'Password is required';
        if (password.length < 6) {
            return 'Password must be at least 6 characters';
        }
        return '';
    };

    // Check if form is valid
    const isFormValid = () => {
        const emailValid = !validateEmail(email);
        const passwordValid = !validatePassword(password);
        return emailValid && passwordValid;
    };

    // Update validation on field changes
    useEffect(() => {
        if (touched.email) {
            setEmailError(validateEmail(email));
        }
        if (touched.password) {
            setPasswordError(validatePassword(password));
        }
    }, [email, password, touched]);

    // Handle blur for validation
    const handleBlur = (field: 'email' | 'password') => {
        setTouched(prev => ({ ...prev, [field]: true }));

        if (field === 'email') {
            setEmailError(validateEmail(email));
        } else if (field === 'password') {
            setPasswordError(validatePassword(password));
        }
    };

    // Handle field changes
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        setFormError('');
        if (touched.email) {
            setEmailError(validateEmail(e.target.value));
        }
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
        setFormError('');
        if (touched.password) {
            setPasswordError(validatePassword(e.target.value));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError('');

        // Mark all fields as touched to show errors
        setTouched({ email: true, password: true });

        // Validate form
        const emailValidation = validateEmail(email);
        const passwordValidation = validatePassword(password);

        setEmailError(emailValidation);
        setPasswordError(passwordValidation);

        if (emailValidation || passwordValidation) {
            setFormError('Please fix the errors above');
            return;
        }

        setLoading(true);

        try {
            // Make API call to login endpoint
            const response = await axios.post(`${API_BASE_URL}/auth/login`, {
                email,
                password
            });

            // Check if we got the access token
            if (response.data && response.data.access_token) {
                // Store the token in localStorage for future API calls
                localStorage.setItem('admin_token', response.data.access_token);

                // Also store token expiration time if provided
                if (response.data.expires_in) {
                    const expiresAt = new Date();
                    expiresAt.setSeconds(expiresAt.getSeconds() + response.data.expires_in);
                    localStorage.setItem('token_expires_at', expiresAt.toISOString());
                }

                // Call the onLogin callback to update parent state
                onLogin();
            } else {
                throw new Error('No access token received');
            }
        } catch (err: any) {
            console.error('Login error:', err);

            // Handle different error scenarios
            if (err.response) {
                // Server responded with error status
                if (err.response.status === 401) {
                    setFormError('Invalid email or password');
                } else if (err.response.status === 400) {
                    setFormError('Bad request. Please check your input');
                } else if (err.response.status === 500) {
                    setFormError('Server error. Please try again later');
                } else {
                    setFormError(`Login failed: ${err.response.data?.message || 'Unknown error'}`);
                }
            } else if (err.request) {
                // Request was made but no response received
                setFormError('Network error. Please check your connection');
            } else {
                // Something else happened
                setFormError('An unexpected error occurred');
            }
        } finally {
            setLoading(false);
        }
    };

    // Helper function to clear localStorage (useful for logout)
    const clearAuthData = () => {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('token_expires_at');
    };

    return (
        <div className={`min-h-screen flex items-center justify-center p-6 relative overflow-hidden ${theme === 'dark' ? 'bg-black' : 'bg-zinc-50'}`}>
            {/* Abstract Background */}
            <div className={`absolute -top-[20%] -right-[10%] w-[60%] h-[60%] rounded-full blur-[120px] pointer-events-none ${theme === 'dark' ? 'bg-violet-500/10' : 'bg-violet-500/10'}`}></div>
            <div className={`absolute -bottom-[20%] -left-[10%] w-[60%] h-[60%] rounded-full blur-[120px] pointer-events-none ${theme === 'dark' ? 'bg-fuchsia-500/10' : 'bg-fuchsia-500/10'}`}></div>

            <div className="max-w-md w-full relative z-10">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-tr from-violet-600 to-fuchsia-600 rounded-2xl mx-auto mb-4 flex items-center justify-center text-white shadow-lg shadow-violet-500/30">
                        <ShieldCheck className="w-8 h-8" />
                    </div>
                    <h2 className={`text-2xl font-black ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>Command Center</h2>
                    <p className={theme === 'dark' ? 'text-zinc-400' : 'text-zinc-500'}>Restricted access for authorized staff</p>
                </div>
                <Card title="Admin Login" theme={theme}>
                    <form onSubmit={handleSubmit}>
                        {formError && (
                            <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${theme === 'dark' ? 'bg-red-900/30 text-red-300 border border-red-800' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                                {formError}
                            </div>
                        )}

                        <div className="mb-5">
                            <Input
                                label="Email"
                                type="email"
                                name="email"
                                value={email}
                                onChange={handleEmailChange}
                                placeholder="admin@wehire.in"
                                required
                                theme={theme}
                            />
                            {emailError && touched.email && (
                                <p className="text-red-500 text-xs mt-1">{emailError}</p>
                            )}
                        </div>

                        <div className="mb-5">
                            <Input
                                label="Password"
                                type="password"
                                name="password"
                                value={password}
                                onChange={handlePasswordChange}
                                placeholder="Enter password"
                                required
                                theme={theme}
                            />
                            {passwordError && touched.password && (
                                <p className="text-red-500 text-xs mt-1">{passwordError}</p>
                            )}
                        </div>

                        <Button
                            type="submit"
                            fullWidth
                            theme={theme}
                            disabled={loading || !isFormValid()}
                            className="relative disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin absolute left-4" />
                                    <span>Authenticating...</span>
                                </>
                            ) : (
                                'Authenticate'
                            )}
                        </Button>

                        {/* Form status indicator (optional) */}
                        <div className={`mt-3 text-xs text-center ${theme === 'dark' ? 'text-zinc-500' : 'text-zinc-400'}`}>
                            {!isFormValid() && (
                                <span>Fill in all required fields correctly to continue</span>
                            )}
                        </div>
                    </form>
                </Card>
            </div>
        </div>
    );
};

export default AdminLogin;